/**
 * Main.js
 * Starting point of the code flow
 */

import {
    Game,
    GameApp,
    AssetManager,
    AudioManager,
    BuildConfig,
    CoreEvents,
    DesignDataManager,
    InputManager,
    LocalizationManager,
    PerformanceMonitor,
    SceneManager,
    SplashScreen,
    UiManager,
    WindowManager,
    InputMethod
} from "pixi_core";

import { LevelEditor } from "pixi_core_editor_level";
import { UiEditor } from "pixi_core_editor_ui";

import { Common } from "./game/Common";
import { OptionsActions } from "./game/OptionsActions";
import { GameDataManager } from "./game/GameDataManager";
import { TutorialManager } from "./Tutorial/TutorialManager";
import { CharacterManager } from "./game/CharacterManager";
import { SettingsManager } from "./game/SettingsManager";
import { CollisionManager } from "./game/collisions/CollisionManager";

import { WindowLevelSelection } from "./Windows/WindowLevelSelection";
import { WindowCinematics } from "./Windows/WindowCinematics";
import { WindowResults } from "./Windows/WindowResults";
import { WindowGame } from "./Windows/WindowGame";
import { WindowTitle } from "./Windows/WindowTitle";
import { AbilityPopup } from "./Windows/AbilityPopup";
import { BossCutIn } from "./Windows/BossCutIn";
import { HeroCutIn } from "./Windows/HeroCutIn";
import { WavePopup } from "./Windows/WavePopup";
import { TutorialPopup } from "./Tutorial/TutorialPopup";

import {
    GameEvents,
    AUDIO_BUTTONS,
    REF_RESOLUTION_WIDTH,
    REF_RESOLUTION_HEIGHT,
    HELP_MARGIN_TOP,
    HELP_MARGIN_BOTTOM,
    HelpPages,
    HELP_CREDITS_MARGIN,
    HELP_CREDITS_AUTOSCROLL_SPEED,
    HELP_CREDITS_AUTOSCROLL_OFFSET
} from "./Constants";

export function main() {
    new GameApp(REF_RESOLUTION_WIDTH, REF_RESOLUTION_HEIGHT);

    Common.settings = new SettingsManager();
    Common.characters = new CharacterManager();
    Common.collisions = new CollisionManager();
    Common.tutorials = new TutorialManager();

    Game.assets = new AssetManager();
    Game.on(CoreEvents.eASSETMANAGER_READY, onAssetManagerReady);
}

/**
 * Initialize saved game data
 */
export function onAssetManagerReady() {
    Game.once(GameEvents.eGAMEDATAMANAGER_READY, onGameDataReady);

    const dataManager = new GameDataManager();
    Common.save = dataManager;
    Game.save = dataManager;
}

/**
 * Initialize main game systems
 */
export function onGameDataReady() {
    Game.windows = new WindowManager(Game.uiRoot);

    const splashScreen = new SplashScreen();
    Game.windows.addContextWindow(splashScreen);

    Game.input = new InputManager();

    splashScreen.waitForEvent("BuildConfig:Ready");
    Game.build = new BuildConfig();

    Game.design = new DesignDataManager();

    Game.audio = new AudioManager();

    splashScreen.waitForEvent("UiManager:Ready");
    Game.ui = new UiManager();

    Game.scenes = new SceneManager();

    splashScreen.waitForEvent("LocalizationManager:Ready");
    Game.localization = new LocalizationManager();

    Game.performance = new PerformanceMonitor();

    Game.windows.addContextWindow(new WindowTitle());
    Game.windows.addContextWindow(new WindowGame());
    Game.windows.addContextWindow(new WindowLevelSelection());
    Game.windows.addContextWindow(new WindowCinematics());

    Game.windows.addPopupWindow(new WindowResults());
    Game.windows.addPopupWindow(new AbilityPopup());
    Game.windows.addPopupWindow(new BossCutIn());
    Game.windows.addPopupWindow(new HeroCutIn());
    Game.windows.addPopupWindow(new WavePopup());
    Game.windows.addPopupWindow(new TutorialPopup());

    new OptionsActions();
    Game.windows.optionsMenu.setAudioButtons(AUDIO_BUTTONS);
    Game.windows.optionsMenu.onlyShowWithFeature("BtnLobby", "multiplayer");
    Game.windows.optionsMenu.setFullScreen();

    Game.windows.changeContext("SplashScreen", { instant: true });
    Game.on(CoreEvents.eSPLASHSCREEN_LOADING_COMPLETED, onLoadingCompleted);
}

export function onLoadingCompleted() {
    if (Game.build.isDebug && Game.build.platform === "html" && Game.build.hasFeature("editor")) {
        new LevelEditor(Game.root);
        new UiEditor(Game.root);
    }

    // Touch/mouse controls share the same pages.
    let touchControls = [];
    touchControls.push({
        content: HelpPages.eCONTROLS_IMAGES_TOUCH,
        subtitle: "IDS_TXT_GAME_CONTROLS",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    touchControls.push({
        content: HelpPages.eCONTROLS_TOUCH,
        subtitle: "IDS_TXT_GAME_CONTROLS",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });

    Game.windows.helpMenu.setControlsPages(InputMethod.eTOUCH, touchControls);

    let keyboardControls = [];
    keyboardControls.push({
        content: HelpPages.eCONTROLS_IMAGES_KEYBOARD,
        subtitle: "IDS_TXT_GAME_CONTROLS",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    keyboardControls.push({
        content: HelpPages.eCONTROLS_KEYS,
        subtitle: "IDS_TXT_GAME_CONTROLS",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });

    Game.windows.helpMenu.setControlsPages(InputMethod.eKEYBOARD, keyboardControls);

    let handsetControls = [];
    handsetControls.push({
        content: HelpPages.eCONTROLS_IMAGES_HANDSET,
        subtitle: "IDS_TXT_GAME_CONTROLS",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });

    Game.windows.helpMenu.setControlsPages(InputMethod.eHANDSET, handsetControls);

    let contentPages = [];

    contentPages.push({
        content: HelpPages.eHOW_TO_PLAY,
        subtitle: "IDS_TXT_HOW_TO_PLAY",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    contentPages.push({
        content: HelpPages.eABILITIES,
        subtitle: "IDS_HELP_TITLE_02",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    contentPages.push({
        content: HelpPages.eAVENGERS,
        subtitle: "IDS_HELP_TITLE_03",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    contentPages.push({
        content: HelpPages.eBUBBLES,
        subtitle: "IDS_HELP_TITLE_04",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    contentPages.push({
        content: HelpPages.eSTATUS_EFFECTS,
        subtitle: "IDS_HELP_TITLE_05",
        marginTop: HELP_MARGIN_TOP,
        marginBottom: HELP_MARGIN_BOTTOM
    });
    contentPages.push({
        content: HelpPages.eCREDITS,
        subtitle: "IDS_TXT_CREDITS",
        autoScroll: { speed: HELP_CREDITS_AUTOSCROLL_SPEED, contentOffsetStart: HELP_CREDITS_AUTOSCROLL_OFFSET },
        marginTop: HELP_CREDITS_MARGIN,
        marginBottom: HELP_CREDITS_MARGIN
    });

    Game.windows.helpMenu.setContentPages(contentPages);

    Game.windows.changeContext("WindowTitle", { removePreviousContext: true });
}
